import { defineStore } from 'pinia';
import { onUnmounted,ref } from 'vue';

import { fetchClientID } from '@/api/clientID';
import router from '@/router';
import { useCartStore } from '@/stores/cart';
import { useCheckoutStore } from '@/stores/checkoutStore';
import type { PlaceOrder } from '@/types';
import { createWebsocketMessage } from '@/utils/createWebsocketMessage';

export const useWebsocketStore = defineStore('websocket', () => {
  const websocket = ref<WebSocket | undefined>(undefined);
  const messages = ref<Array<{ rtoken: string | undefined; id: number; data: object; action: string }>>([]);
  const desiredState = ref('disconnected');
  const state = ref('disconnected');
  const rtoken = ref<string | undefined>(undefined);
  const id = ref<number>(0);
  const url = import.meta.env.DEV
    ? "ws://" + location.host + "/of2/ws/ticketsale"
    : "wss://" + location.hostname + "/of2/ws/ticketsale";
  const pingInterval = ref<number | undefined>(undefined);
  const lastPong = ref<number>(Date.now());
  const lastActive = ref<number | undefined>(undefined);
  const websocketTime = ref<number>(Date.now());
  const clientTime = ref<number>(Date.now());
  const clientID = ref<string | undefined>(undefined);
  const pongCount = ref<number>(0);

  function connect() {
    websocket.value = new WebSocket(url);

    websocket.value.onopen = () => {
      stateChanged();
    };

    websocket.value.onmessage = (event) => {
      handleMessage(event);
    };

    websocket.value.onclose = () => {
      stateChanged();
    };

    websocket.value.onerror = () => {
      stateChanged();
    };
    state.value = 'connecting';
  }

  function handleMessage(event: MessageEvent) {
    const message = JSON.parse(event.data);
    console.log("Received WebSocket message:", message);
    console.log("Current WebSocket state:", state.value);
    if(state.value === 'authenticating'){
      if(message.action === 'rtoken'){
        rtoken.value = message.rtoken;
        saveRtoken();
        state.value = 'connected';
        //Send waiting message
        // console.log("Sending waiting messages, count: " + messages.value.length);
        // console.log(messages.value);
        while (messages.value.length > 0) {
          const msg = messages.value.shift();
          // console.log(msg);
          if (msg) {
            msg.rtoken = rtoken.value;
            if(websocket.value) {
              websocket.value.send(JSON.stringify(msg));
            }
          }
        }
      }
      else {
        console.error("Bad action: " + message.action);
        if(message.action == 'badclientid'){
          getClientID(true);
        }
      }
    }
    else if(state.value == 'connected'){
      // Handle messages for the connected state
      if(message.action == 'pong'){
        syncTime(message);
        lastPong.value = getRealTime();
        pongCount.value += 1;
        if(pongCount.value > 5){
          //Practically anywhere not in checkout flow should close the connection if cart is empty
          if(!router.currentRoute.value.path.includes("checkout")){
            // Check if basket is empty, if so close connection
            const cartStore = useCartStore();
            if(cartStore.isCartEmpty()){
              pongCount.value = 0;
              close();
            }
          }
        }
      }
      else if (message.action == 'event') {
        const checkoutStore = useCheckoutStore();
        checkoutStore.setCheckoutData(message.data);
      }
      else if (message.action == 'reservetickets') {
        const cartStore = useCartStore();
        cartStore.updateReservedTickets(message.data);
      }
      else if (message.action == 'startpurchase') {
        const checkoutStore = useCheckoutStore();
        checkoutStore.setStartPurchaseData(message.data);
        const cartStore = useCartStore();
        cartStore.updateCustomFieldsStartPurchase(message.data);
        // console.log("Start purchase data received:", message.data);
      }
      else if (message.action == 'orderdata') {
        const checkoutStore = useCheckoutStore();
        if((message.data.errors && Object.keys(message.data.errors).length > 0) || (message.data.custom_field_errors && Object.keys(message.data.custom_field_errors).length > 0)){
          checkoutStore.orderErrors = message.data.errors;
          console.log("Order data errors:", message.data.errors);
          console.log("checkoutStore.orderErrors:", checkoutStore.orderErrors); 
          if(message.data.custom_field_errors){
            const cartStore = useCartStore();
            console.log('cart items before custom field errors:', cartStore.items);
            checkoutStore.customFieldErrors = message.data.custom_field_errors;
            console.log("Custom field errors:", message.data.custom_field_errors);
          }
        }
        else if(message.data.ok == 0) {
          alert("Error validating order data. Please check your input and try again.");
        }
        else if(checkoutStore.startPurchaseData.dankort_discount && checkoutStore.startPurchaseData.dankort_discount > 0){
          checkoutStore.paymentModal = true;
        }
        else {
          doPlaceOrder('creditcard');
        }
        // console.log("Order data received:", message.data);
      }
      else if (message.action == 'placeorder') {
        const checkoutStore = useCheckoutStore();
        checkoutStore.placeOrderPaymentVars = message.data.payment_vars;
        if(message.data.error){
          alert("Error placing order: " + message.data.error);
        }
        else if(message.data.ok == 1 && message.data.proceed == 'gotoreceipt'){
          router.push("/receipt?rtoken=" + rtoken.value);
        }
        else if( message.data.ok == 1 && checkoutStore.startPurchaseData.psp == 'quickpay10'){
          checkoutStore.startQuickPay10Payment();
        }
        else if( message.data.ok == 1 && checkoutStore.startPurchaseData.psp == 'epay'){
          checkoutStore.startEpayPayment();
        }
        else {
          alert("Unknown error placing order.");
        }
      }
      else if (message.action == 'basket') {
        const cartStore = useCartStore();
        cartStore.setSimpleBasketData(message.data);
      }
      else if (message.action == 'receipt') {
        const cartStore = useCartStore();
        cartStore.setReceiptOrder(message.data);
      }
      else if (message.action == 'confirmpayment') {
        const cartStore = useCartStore();
        cartStore.setReceiptMessage(message.data);
      }
      else if (message.action == 'pdfready') {
        const cartStore = useCartStore();
        cartStore.pdfReady = true;
      }
      else if (message.action == 'vouchercode'){
        const checkoutStore = useCheckoutStore();
        const cartStore = useCartStore();

        checkoutStore.setVouchercodeData(message.data);
        if(message.data.ok){
          cartStore.resendReservedTickets();
        }
        // Handle voucher code response tomorrow
      }
      else if (message.action == 'unpaidorder'){
        const cartStore = useCartStore();
        cartStore.setUnpaidOrder(message.data);
      }
      else if (message.action == 'endconnection') {
        close();
      }
      else {
        console.error("Bad action: " + message.action);
      }
    }
    // else {
    //   console.log("Received WebSocket message in unknown state:", message);
    // }

  }

  function stateChanged() {
    const readyState = getWebsocketReadyState();
    if (readyState === WebSocket.OPEN) {
      state.value = 'open';
    } 
    else if (readyState === WebSocket.CLOSING) {
      state.value = 'closing';
    }
    else if (readyState === WebSocket.CLOSED) {
      state.value = 'closed';
      websocket.value = undefined;
      if(desiredState.value == 'disconnected'){
        rtoken.value = undefined;
        unloadRtoken();
        unloadClientID();
      }
    }
    checkWebsocketState();
    if(desiredState.value == 'connected' && state.value == 'closed'){
      setTimeout(() => {
        state.value = 'disconnected';
        checkWebsocketState();
      }, 10000);
    }
    else if (desiredState.value == 'disconnected' && state.value == 'closed'){
      state.value = 'disconnected';
      checkWebsocketState();
    }
  }

  function doPlaceOrder(cardtype: string) {
    const checkoutStore = useCheckoutStore();
    const PlaceOrderData: PlaceOrder = checkoutStore.orderData as PlaceOrder;
    PlaceOrderData.cardtype = cardtype;
    PlaceOrderData.language = checkoutStore.getCheckoutLanguage();
    PlaceOrderData.is_doorsale = false;
    send('placeorder', PlaceOrderData, undefined);
  }

  function checkWebsocketState () {
      if(desiredState.value == 'connected'){
        if(state.value == 'disconnected'){
          if(websocket.value === undefined){
            connect();
          }
        }
        else if (state.value == 'open') {
          id.value = 0;
          if(pingInterval.value !== undefined){
            clearInterval(pingInterval.value);
            pingInterval.value = undefined;
          }
          pingInterval.value = setInterval(() => {
            doPing();
          }, 30000);
          lastPong.value = getRealTime();
          getClientID().then(() => {
            if(clientID.value != undefined){
              state.value = 'authenticating';
              restoreRtoken();
              send('rtoken', { }, clientID.value);
            }
            else {
              console.error("No client ID");
            }
          });
        }
      }
      else if (desiredState.value == 'disconnected'){
        if(state.value == 'connecting' || state.value == 'open' || state.value == 'connected' || state.value == 'authenticating'){
          disconnect();
          state.value = 'closing';
        }
      }
  }

  function doPing() {
    if (websocket.value && state.value == 'connected') {
      if(getRealTime() - lastPong.value > 59000){
        disconnect();
        clearInterval(pingInterval.value);
        return;
      }
      else {
        const href = location.href;
        const isBasketEmpty = true; // Check if the basket is empty, needs implementation
        if(lastActive.value == undefined || href.includes("checkout") || href.includes("payment") || !isBasketEmpty){
          lastActive.value = getRealTime();
        }
        else if (getRealTime() - lastActive.value > 899000) {
          close();
          clearInterval(pingInterval.value);
          return;
        }
      }
      send('ping', {}, undefined);
    }
    else if (state.value != 'open' && state.value != 'authenticating') {
      // Handle other states
    }
  }

  function syncTime(msg: { data: { servertime: string } }) {
    const dateTime = new Date();
    clientTime.value = dateTime.getTime();
    if (msg.data && msg.data.servertime) {
      websocketTime.value = parseInt(msg.data.servertime, 10) * 1000;
    }
    else {
      console.error("No server time in sync message");
      websocketTime.value = dateTime.getTime();
    }
  }

  function getRealTime(): number {
    const date = new Date();
    const dateTime = date.getTime();

    const delta = websocketTime.value - clientTime.value;

    const realTime = dateTime + delta;
    return realTime;
  }

  function getWebsocketReadyState(): number {
    return websocket.value?.readyState ?? WebSocket.CLOSED;
  }

  function send(action: string, data: object, clientID: string | undefined) {
    if (websocket.value && (state.value == 'connected' || (state.value == 'authenticating' && action == 'rtoken'))) {
      websocket.value.send(JSON.stringify(createWebsocketMessage(action, data, clientID)));
    }
    else {
      messages.value.push(createWebsocketMessage(action, data, clientID));
    }
    console.log(action, data, clientID);
  }

  function close(){
    desiredState.value = 'disconnected';
    checkWebsocketState();
  }

  function open(){
    desiredState.value = 'connected';
    checkWebsocketState();
  }

  function disconnect() {
    websocket.value?.close();
  }

  async function getClientID(reload: boolean = false) {
    if(clientID.value != undefined && !reload){
      return;
    }
    //Get client ID from storage or API
    const now = Date.now();
    let storedClientID = localStorage.getItem('clientID');
    let storedClientIDTimestamp = localStorage.getItem('clientIDTimestamp');
    //If we cannot get it from localstoage, get it from session storage
    if(!storedClientID){
      storedClientID = sessionStorage.getItem('clientID');
      storedClientIDTimestamp = sessionStorage.getItem('clientIDTimestamp');
    }
    if(storedClientID && storedClientIDTimestamp){
      const timestamp = parseInt(storedClientIDTimestamp, 10);
      //Check if client ID is older than 15 minutes
      if(now - timestamp < 15 * 60 * 1000){
        clientID.value = storedClientID;
        return;
      }
    }
    clientID.value = await fetchClientID();
  }

  function unloadClientID () {
    localStorage.removeItem('clientID');
    localStorage.removeItem('clientIDTimestamp');
    sessionStorage.removeItem('clientID');
    sessionStorage.removeItem('clientIDTimestamp');
  }

  function saveRtoken () {
    // save rtoken to local and session storage
    const rtokenValue = rtoken.value;
    if(!rtokenValue){
      return;
    }
    localStorage.setItem('rtoken', rtokenValue);
    localStorage.setItem('rtokenTimestamp', Date.now().toString());
    sessionStorage.setItem('rtoken', rtokenValue);
    sessionStorage.setItem('rtokenTimestamp', Date.now().toString());
  }

  function unloadRtoken () {
    localStorage.removeItem('rtoken');
    localStorage.removeItem('rtokenTimestamp');
    sessionStorage.removeItem('rtoken');
    sessionStorage.removeItem('rtokenTimestamp');
  }

  function restoreRtoken () {
    let storedRtoken = localStorage.getItem('rtoken');
    let storedRtokenTimestamp = localStorage.getItem('rtokenTimestamp');
    if(!storedRtoken || !storedRtokenTimestamp){
      storedRtoken = sessionStorage.getItem('rtoken');
      storedRtokenTimestamp = sessionStorage.getItem('rtokenTimestamp');
    }
    if(storedRtoken && storedRtokenTimestamp){
      const timestamp = parseInt(storedRtokenTimestamp, 10);
      //Check if rtoken is older than 15 minutes
      if(Date.now() - timestamp < 15 * 60 * 1000){
        rtoken.value = storedRtoken;
        return;
      }
    }
    else {
      return undefined;
    }
  }

  function restoreConnection(){
    //If we have a client ID, try to reconnect
    getClientID().then(() => {
      if(clientID.value != undefined){
        open();
      }
    });
  }

  onUnmounted(() => {
    disconnect();
  });

  return {
    open,
    close,
    send,
    id,
    rtoken,
    doPlaceOrder,
    restoreConnection,
  };
});